/**
 * Host-based product brand. Keep hostname rules in sync with api/src/brand.ts.
 *
 * agentmeetings.ai and www.agentmeetings.ai → AgentMeetings.
 * takenotes.us, localhost, and anything else → TakeNotes.
 */

export type BrandId = 'takenotes' | 'agentmeetings';

export interface Brand {
  id: BrandId;
  name: string;
  domain: string;
  /** Short line under the login wordmark and in the footer. No trailing period. */
  tagline: string;
  /** Compact mark: favicon and auth cards. */
  markSrc: string;
  /**
   * Full header lockup image.
   * TakeNotes: locked PNG (no URL line; that variant is Zoom-only).
   * AgentMeetings: George's handwritten Meetings lockup.
   */
  headerLockupSrc: string | null;
  /** HMAC header documented in this brand's developer chrome. Both names are sent. */
  signatureHeader: string;
  mcpServerKey: string;
  hookExampleHost: string;
}

export const TAKENOTES_BRAND: Brand = {
  id: 'takenotes',
  name: 'TakeNotes',
  domain: 'takenotes.us',
  tagline: 'Meeting notes for your team',
  markSrc: '/logo/takenotes-logo-b-mark.svg',
  headerLockupSrc: '/logo/takenotes-logo-locked.png',
  signatureHeader: 'X-TakeNotes-Signature',
  mcpServerKey: 'takenotes',
  hookExampleHost: 'takenotes',
};

export const AGENTMEETINGS_BRAND: Brand = {
  id: 'agentmeetings',
  name: 'AgentMeetings',
  domain: 'agentmeetings.ai',
  tagline: 'Meeting memory for agents',
  markSrc: '/logo/am-logo-c-mark.svg',
  headerLockupSrc: '/logo/am-logo-locked-header.png',
  signatureHeader: 'X-AgentMeetings-Signature',
  mcpServerKey: 'agentmeetings',
  hookExampleHost: 'agentmeetings',
};

/** Bare hostname, no port, no trailing dot. */
export function normalizeHostname(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/\.$/, '').replace(/:\d+$/, '');
}

export function brandIdFromHostname(hostname: string): BrandId {
  const host = normalizeHostname(hostname);
  if (host === 'agentmeetings.ai' || host === 'www.agentmeetings.ai') return 'agentmeetings';
  return 'takenotes';
}

export function brandForHostname(hostname: string): Brand {
  return brandIdFromHostname(hostname) === 'agentmeetings' ? AGENTMEETINGS_BRAND : TAKENOTES_BRAND;
}

export function currentBrand(): Brand {
  const host = typeof window !== 'undefined' ? window.location.hostname : 'takenotes.us';
  return brandForHostname(host);
}

/** "Ask an AgentMeetings admin …" / "Ask a TakeNotes admin …" */
export function askProductAdmin(rest: string): string {
  const name = currentBrand().name;
  const article = /^[aeiou]/i.test(name) ? 'an' : 'a';
  return `Ask ${article} ${name} admin ${rest}`;
}

/** Tab title fragment for the current route. Empty means the product name alone. */
export function documentHeading(pathname: string, hash: string, authed: boolean): string {
  if (!authed || pathname === '/login') return 'Sign in';
  if (pathname.startsWith('/meetings/')) return 'Meeting';
  if (pathname === '/settings/developers') {
    if (hash === '#webhooks') return 'Webhooks';
    if (hash === '#logs') return 'Logs';
    if (hash === '#agents') return 'Agents';
    if (hash === '#mcp') return 'MCP';
    if (hash === '#api-keys') return 'API keys';
    return 'Developers';
  }
  const titles: Record<string, string> = {
    '/': 'Meeting Library',
    '/join': 'New Meeting',
    '/settings/calendar': 'Calendar',
    '/settings/keywords': 'Keywords',
    '/settings/models': 'Models',
    '/settings/integrations': 'Integrations',
    '/settings/account': 'Account',
    '/admin': 'Admin',
    '/docs': 'Docs',
  };
  return titles[pathname] ?? '';
}

/** Point the tab title and favicon at the active brand. TakeNotes keeps the HTML icons. */
export function applyDocumentBrand(brand: Brand, pageTitle: string): void {
  document.title = pageTitle ? `${pageTitle} · ${brand.name}` : brand.name;
  if (brand.id !== 'agentmeetings') return;
  document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((link) => {
    link.href = brand.markSrc;
    link.type = 'image/svg+xml';
    link.removeAttribute('media');
    link.removeAttribute('sizes');
  });
}
